"use client"

import { startTransition, useState, useCallback, useEffect, useRef } from "react"
import { MessageCircle, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useChat } from "@/hooks/use-chat"
import { ChatPanel } from "./chat-panel"
import "./chat-glass.css"
import { useIsMobile } from "@/components/ui/use-mobile"
import { useBottomDockInset } from "@/hooks/use-bottom-dock-inset"
import { trackEvent } from "@/lib/analytics"
import { useChatAvoidance } from "./use-chat-avoidance"
import {
  BASE_BOTTOM,
  NUDGE_BOTTOM,
  OVERLAY_EVENTS,
  OVERLAY_ENTRANCE_DELAY,
  safeBottom,
} from "@/lib/overlay-constants"

// Match the panel's transition-all duration in chat-panel.tsx so the close
// animation finishes before we unmount.
const PANEL_CLOSE_ANIMATION_MS = 300

const NUDGE_SEEN_KEY = 'chat_nudge_seen'

function markNudgeSeen() {
  try { sessionStorage.setItem(NUDGE_SEEN_KEY, 'true') } catch {}
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  // `isClosing` keeps the panel mounted for one animation cycle after isOpen
  // flips to false, so the slide-out transition still plays. Without the
  // mount gate, the panel was always in the DOM under `opacity-0`, which
  // PostHog's rrweb player misrenders with Tailwind v4 — see
  // C:\Users\swift\.claude\plans\please-look-into-this-playful-puffin.md.
  const [isClosing, setIsClosing] = useState(false)
  const [fabReady, setFabReady] = useState(false)
  const [showNudge, setShowNudge] = useState(false)
  const [nudgeDismissed, setNudgeDismissed] = useState(false)
  // Stops the FAB attention-pulse once the user has opened the chat at least
  // once. Distinct from `nudgeDismissed` so the pulse keeps drawing the eye
  // even after the nudge auto-hides, until there's a real engagement.
  const [hasEngaged, setHasEngaged] = useState(false)

  // Hydrate dismissal from sessionStorage so the nudge fires once per browser
  // session, not once per route mount. Without this, every navigation that
  // remounts the widget would re-trigger the auto-pop timer.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(NUDGE_SEEN_KEY) === 'true') {
        setNudgeDismissed(true)
      }
    } catch {}
  }, [])
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const fabRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const isMobile = useIsMobile()
  const bottomDockInset = useBottomDockInset()
  const avoidContent = useChatAvoidance(isMobile)
  const chat = useChat()

  // Hide when results section is on screen (conversion focus)
  const [hideForResults, setHideForResults] = useState(false)
  useEffect(() => {
    const check = () => setHideForResults(!!document.getElementById('results-section'))
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  // Overlay coordination: the header owns the drawer and dispatches these
  // stable window events so independently mounted overlays never share the
  // same mobile hit area.
  useEffect(() => {
    const handleNavOpen = () => {
      setMobileNavOpen(true)
      setShowNudge(false)
    }
    const handleNavClose = () => setMobileNavOpen(false)

    window.addEventListener(OVERLAY_EVENTS.MOBILE_NAV_OPENED, handleNavOpen)
    window.addEventListener(OVERLAY_EVENTS.MOBILE_NAV_CLOSED, handleNavClose)
    return () => {
      window.removeEventListener(OVERLAY_EVENTS.MOBILE_NAV_OPENED, handleNavOpen)
      window.removeEventListener(OVERLAY_EVENTS.MOBILE_NAV_CLOSED, handleNavClose)
    }
  }, [])

  // Delayed entrance animation + scroll gate on mobile — waits for the
  // homepage cascade to settle before the FAB appears.
  const [hasScrolled, setHasScrolled] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setFabReady(true), OVERLAY_ENTRANCE_DELAY)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!isMobile) {
      setHasScrolled(true)
      return
    }
    const handleScroll = () => {
      // Non-urgent: sync scroll-driven setState during hydration discards
      // pending Suspense boundaries → recoverable React #418.
      if (window.scrollY > 300) startTransition(() => setHasScrolled(true))
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMobile])

  // Show nudge bubble after FAB appears, then auto-dismiss.
  // Marks the nudge as seen in sessionStorage on auto-hide so subsequent
  // navigations within the session skip the auto-pop entirely.
  useEffect(() => {
    if (isMobile) {
      setShowNudge(false)
      return
    }
    if (!fabReady || nudgeDismissed) return
    const showTimer = setTimeout(() => setShowNudge(true), 1500)
    const hideTimer = setTimeout(() => {
      setShowNudge(false)
      setNudgeDismissed(true)
      markNudgeSeen()
    }, 5250)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [fabReady, isMobile, nudgeDismissed])

  const handleOpen = useCallback(() => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    setIsOpen(true)
    setIsClosing(false)
    setShowNudge(false)
    setNudgeDismissed(true)
    setHasEngaged(true)
    markNudgeSeen()
    trackEvent('chat_widget_opened', {
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    })
  }, [])
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleClose = useCallback(() => {
    setIsOpen(false)
    setIsClosing(true)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      setIsClosing(false)
    }, PANEL_CLOSE_ANIMATION_MS)
    setTimeout(() => {
      const target = fabRef.current ?? returnFocusRef.current
      if (target?.isConnected) target.focus({ preventScroll: true })
    }, 100)
  }, [])
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])
  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose()
    } else {
      handleOpen()
    }
  }, [isOpen, handleOpen, handleClose])

  // Lock body scroll on mobile when panel is open (iOS-compatible)
  const scrollPositionRef = useRef(0)
  useEffect(() => {
    if (!isOpen || !isMobile) return

    scrollPositionRef.current = window.scrollY

    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollPositionRef.current}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    return () => {
      const scrollY = scrollPositionRef.current
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      if (scrollY > 0) {
        window.scrollTo({ top: scrollY, behavior: 'instant' })
      }
    }
  }, [isOpen, isMobile])

  if (hideForResults) return null

  const fabVisible =
    fabReady &&
    hasScrolled &&
    !(isOpen && isMobile) &&
    !(isMobile && (mobileNavOpen || avoidContent))
  const fabBottom = safeBottom(BASE_BOTTOM + bottomDockInset)
  const nudgeBottom = safeBottom(NUDGE_BOTTOM + bottomDockInset)

  return (
    <>
      {(isOpen || isClosing) && (
        <ChatPanel
          isOpen={isOpen}
          onClose={handleClose}
          messages={chat.messages}
          activeTools={chat.activeTools}
          isStreaming={chat.isStreaming}
          error={chat.error}
          onSend={chat.sendMessage}
          onClearError={chat.clearError}
        />
      )}

      {/* Nudge bubble */}
      {!isOpen && !isMobile && (
        <div
          className={cn(
            "fixed right-[80px] md:right-[88px] z-60",
            showNudge &&
              !nudgeDismissed &&
              !isMobile &&
              hasScrolled &&
              !mobileNavOpen &&
              !avoidContent
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-4 pointer-events-none"
          )}
          style={{
            bottom: nudgeBottom,
            transition: 'opacity 300ms, transform 300ms, bottom 300ms',
          }}
        >
          <button
            onClick={() => {
              setNudgeDismissed(true)
              setShowNudge(false)
              markNudgeSeen()
              handleOpen()
            }}
            aria-controls="lawn-care-chat-dialog"
            aria-expanded="false"
            className="bg-white text-gray-800 text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2"
          >
            Got a lawn question?
          </button>
          {/* Speech bubble tail */}
          <div className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 bg-white border-r border-b border-gray-200 -rotate-45" />
        </div>
      )}

      {/* FAB — mount-gated so the DOM doesn't hold a hidden copy that
          PostHog rrweb can misrender (Tailwind v4 + opacity-0 issue). */}
      {fabVisible && (
        <button
          ref={fabRef}
          onClick={handleToggle}
          className={cn(
            "lcw-fab fixed right-3 md:right-6 z-60 w-14 h-14",
            "focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
            isOpen && "is-open",
            hasEngaged && "is-engaged"
          )}
          style={{
            bottom: fabBottom,
            transition:
              'bottom 300ms, transform 0.25s var(--ease-standard), box-shadow 0.25s',
          }}
          aria-label={isOpen ? "Close chat" : "Open chat"}
          aria-controls="lawn-care-chat-dialog"
          aria-expanded={isOpen}
        >
          <span className="lcw-fab__open">
            <MessageCircle className="w-6 h-6" strokeWidth={2} />
          </span>
          <span className="lcw-fab__close">
            <X className="w-6 h-6" />
          </span>
        </button>
      )}
    </>
  )
}
